import { useCallback, useEffect, useState } from 'react';
import { expectArray } from '../../../apiShape.js';
import { toUserFacingMessage } from '../../../userFacingError.js';
/**
 * useBasecampExportState — Project → To-do list, the Trello Board → List
 * shape. A project carries the Basecamp account it lives in, because one
 * 37signals login can reach several accounts and the export destination
 * needs the account id as well (lib/basecamp.ts).
 */
export function useBasecampExportState(runtime) {
    const [projects, setProjects] = useState([]);
    const [projectId, setProjectId] = useState('');
    const [todolists, setTodolists] = useState([]);
    const [todolistId, setTodolistId] = useState('');
    const [todolistsLoading, setTodolistsLoading] = useState(false);
    const [todolistsError, setTodolistsError] = useState(null);
    const [todolistsRetry, setTodolistsRetry] = useState(0);
    const accountId = projects.find(project => project.id === projectId)?.accountId ?? '';
    // Clear the child in the gesture itself, before the next effect can run, so
    // Export never sees a list from the previous project during loading.
    const chooseProject = useCallback((value) => {
        setProjectId(value);
        setTodolistId('');
        setTodolists([]);
        setTodolistsError(null);
    }, []);
    const onDataLoaded = useCallback((items) => {
        setProjects(items);
        chooseProject(items[0]?.id ?? '');
    }, [chooseProject]);
    useEffect(() => {
        if (!projectId || !accountId) {
            setTodolistsLoading(false);
            return;
        }
        let cancelled = false;
        setTodolistsLoading(true);
        setTodolistsError(null);
        runtime.api.getBasecampTodolists(accountId, projectId).then(response => {
            if (cancelled)
                return;
            const items = expectArray(response, 'Basecamp to-do lists');
            setTodolists(items);
            setTodolistId(items[0]?.id ?? '');
        }).catch(error => {
            if (cancelled)
                return;
            setTodolists([]);
            setTodolistId('');
            setTodolistsError(toUserFacingMessage(error, "Couldn't load Basecamp to-do lists. Try again."));
        }).finally(() => { if (!cancelled)
            setTodolistsLoading(false); });
        return () => { cancelled = true; };
    }, [projectId, accountId, todolistsRetry, runtime]);
    const reset = useCallback(() => { setProjects([]); chooseProject(''); }, [chooseProject]);
    return { projects, projectId, accountId, todolists, todolistId, setTodolistId, chooseProject,
        todolistsLoading, todolistsError, onDataLoaded, reset,
        retryTodolists: () => setTodolistsRetry(n => n + 1) };
}
//# sourceMappingURL=useBasecampExportState.js.map